import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';
import TopNavigationBar from './components/ui/TopNavigationBar';
import MobileTabBar from './components/ui/MobileTabBar';
import BackToTop from './components/ui/BackToTop';
import DeveloperDashboard from './pages/developer-dashboard/index';

// Lazy load pages
const Homepage = lazy(() => import('./pages/homepage'));
const PersonalisedFeed = lazy(() => import('./pages/personalised-feed'));
const StoryReadingPage = lazy(() => import('./pages/story-reading-page'));
const StoryEditor = lazy(() => import('./pages/story-editor'));
const WriterProfile = lazy(() => import('./pages/writer-profile'));
const MyStories = lazy(() => import('./pages/my-stories'));
const AnalyticsDashboard = lazy(() => import('./pages/analytics-dashboard'));
const AdminDashboard = lazy(() => import('./pages/admin-dashboard'));
const AccountSettings = lazy(() => import('./pages/account-settings'));
const ReadingList = lazy(() => import('./pages/reading-list'));
const TopicBrowse = lazy(() => import('./pages/topic-browse'));
const CollaborativeWriting = lazy(() => import('./pages/collaborative-writing'));
const PublicationManagement = lazy(() => import('./pages/publication-management'));
const Login = lazy(() => import('./pages/login'));
const SignUp = lazy(() => import('./pages/sign-up'));
const PasswordReset = lazy(() => import('./pages/password-reset'));
const Sitemap = lazy(() => import('./pages/sitemap'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));
const AdminAlertsPage = lazy(() => import('./pages/admin-alerts'));
const AdminSettings = lazy(() => import('./pages/admin-settings'));
const AdminNotifications = lazy(() => import('./pages/admin-notifications'));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      <p className="text-muted-foreground font-interface text-sm">Loading...</p>
    </div>
  </div>
);

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Public routes */}
          <Route path="/" element={<Navigate to="/homepage" replace />} />
          <Route path="/homepage" element={<><TopNavigationBar /><Suspense fallback={<PageLoader />}><Homepage /></Suspense><MobileTabBar /></>} />
          <Route path="/login" element={<Suspense fallback={<PageLoader />}><Login /></Suspense>} />
          <Route path="/sign-up" element={<Suspense fallback={<PageLoader />}><SignUp /></Suspense>} />
          <Route path="/password-reset" element={<Suspense fallback={<PageLoader />}><PasswordReset /></Suspense>} />
          <Route path="/story/:id" element={<><TopNavigationBar /><Suspense fallback={<PageLoader />}><StoryReadingPage /></Suspense><MobileTabBar /></>} />
          <Route path="/writer/:id" element={<><TopNavigationBar /><Suspense fallback={<PageLoader />}><WriterProfile /></Suspense><MobileTabBar /></>} />
          <Route path="/topic-browse" element={<><TopNavigationBar /><Suspense fallback={<PageLoader />}><TopicBrowse /></Suspense><MobileTabBar /></>} />
          <Route path="/sitemap" element={<><TopNavigationBar /><Suspense fallback={<PageLoader />}><Sitemap /></Suspense></>} />
          <Route path="/unauthorized" element={<Suspense fallback={<PageLoader />}><Unauthorized /></Suspense>} />

          {/* Protected routes - require authentication */}
          <Route path="/personalised-feed" element={<ProtectedRoute><TopNavigationBar /><Suspense fallback={<PageLoader />}><PersonalisedFeed /></Suspense><MobileTabBar /></ProtectedRoute>} />
          <Route path="/reading-list" element={<ProtectedRoute><TopNavigationBar /><Suspense fallback={<PageLoader />}><ReadingList /></Suspense><MobileTabBar /></ProtectedRoute>} />
          <Route path="/account-settings" element={<ProtectedRoute><TopNavigationBar /><Suspense fallback={<PageLoader />}><AccountSettings /></Suspense><MobileTabBar /></ProtectedRoute>} />

          {/* Writer-only routes */}
          <Route path="/story-editor" element={<ProtectedRoute allowedRoles={['writer', 'admin']}><TopNavigationBar /><Suspense fallback={<PageLoader />}><StoryEditor /></Suspense></ProtectedRoute>} />
          <Route path="/story-editor/:id" element={<ProtectedRoute allowedRoles={['writer', 'admin']}><TopNavigationBar /><Suspense fallback={<PageLoader />}><StoryEditor /></Suspense></ProtectedRoute>} />
          <Route path="/my-stories" element={<ProtectedRoute allowedRoles={['writer', 'admin']}><TopNavigationBar /><Suspense fallback={<PageLoader />}><MyStories /></Suspense><MobileTabBar /></ProtectedRoute>} />
          <Route path="/collaborative-writing" element={<ProtectedRoute allowedRoles={['writer', 'admin']}><TopNavigationBar /><Suspense fallback={<PageLoader />}><CollaborativeWriting /></Suspense><MobileTabBar /></ProtectedRoute>} />
          <Route path="/publication-management" element={<ProtectedRoute allowedRoles={['writer', 'admin']}><TopNavigationBar /><Suspense fallback={<PageLoader />}><PublicationManagement /></Suspense><MobileTabBar /></ProtectedRoute>} />

          {/* Admin-only routes */}
          <Route path="/analytics-dashboard" element={<ProtectedRoute allowedRoles={['admin']}><TopNavigationBar /><Suspense fallback={<PageLoader />}><AnalyticsDashboard /></Suspense><MobileTabBar /></ProtectedRoute>} />
          <Route path="/developer-dashboard" element={<ProtectedRoute allowedRoles={['admin']}><TopNavigationBar /><Suspense fallback={<PageLoader />}><DeveloperDashboard /></Suspense><MobileTabBar /></ProtectedRoute>} />
          <Route path="/admin-dashboard" element={<ProtectedRoute allowedRoles={['admin']}><TopNavigationBar /><Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense><MobileTabBar /></ProtectedRoute>} />
          <Route path="/admin-alerts" element={<ProtectedRoute allowedRoles={['admin']}><TopNavigationBar /><Suspense fallback={<PageLoader />}><AdminAlertsPage /></Suspense><MobileTabBar /></ProtectedRoute>} />
          <Route path="/admin-settings" element={<ProtectedRoute allowedRoles={['admin']}><TopNavigationBar /><Suspense fallback={<PageLoader />}><AdminSettings /></Suspense><MobileTabBar /></ProtectedRoute>} />
          <Route path="/admin-notifications" element={<ProtectedRoute allowedRoles={['admin']}><TopNavigationBar /><Suspense fallback={<PageLoader />}><AdminNotifications /></Suspense><MobileTabBar /></ProtectedRoute>} />

          {/* 404 */}
          <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
        </RouterRoutes>
        <BackToTop />
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
